// NodeMissionTaskChecklistActionModal.jsx

import React, { useCallback, useMemo, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Utilities
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { isNodelistAllowMultiple, normalizeNodelistValue } from 'utilities/NodelistField';
import { resolveChecklistActionModalType } from 'missions/taskmodal/checklistItemActions';
import {
  getDataSectionName,
  resolveFieldValue,
} from 'views/table/utils/tableFieldUtils';

// Components
import MemberPickerModal from 'components/memberpicker/MemberPickerModal';
import NodeDateModal from 'node/form/fields/date/NodeDateModal';
import NodeDateRangeModal from 'node/form/fields/daterange/NodeDateRangeModal';
import NodeDateTimeModal from 'node/form/fields/datetime/NodeDateTimeModal';
import NodeDateTimeRangeModal from 'node/form/fields/datetimerange/NodeDateTimeRangeModal';
import NodeLocationModal from 'node/form/fields/location/NodeLocationModal';
import NodeMoneyModal from 'node/form/fields/money/NodeMoneyModal';
import NodeMultiselectModal from 'node/form/fields/multiselect/NodeMultiselectModal';
import NodeNodelistModal from 'node/form/fields/nodelist/NodeNodelistModal';
import NodeNodelistSingleModal from 'node/form/fields/nodelist/NodeNodelistSingleModal';
import NodePercentageModal from 'node/form/fields/percentage/NodePercentageModal';
import NodeSelectModal from 'node/form/fields/select/NodeSelectModal';
import NodeTagsModal from 'node/form/fields/tags/NodeTagsModal';

/**
 * NodeMissionTaskChecklistActionModal
 *
 * Opens the table-equivalent field editor for a checklist "Set Field" action
 * against the workflow host record (e.g. PROJECT).
 *
 * @param {object|null} hostNode
 * @param {object|null} fieldDef
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {function} [onHostNodeChange]
 * @returns {ReactNode}
 */
const NodeMissionTaskChecklistActionModal = React.memo(({
  hostNode = null,
  fieldDef = null,
  isOpen = false,
  onClose,
  onHostNodeChange = null,
}) => {
  const { user } = useUser();
  const [saving, setSaving] = useState(false);

  const typeKey = String(hostNode?.type || '').trim();
  const dataSection = useMemo(() => getDataSectionName(typeKey), [typeKey]);
  const modalType = resolveChecklistActionModalType(fieldDef);
  const fieldLabel = String(fieldDef?.label || fieldDef?.name || 'Field').trim() || 'Field';
  const fieldValue = useMemo(
    () => resolveFieldValue(hostNode, fieldDef, dataSection),
    [dataSection, fieldDef, hostNode],
  );

  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const persistValue = useCallback(async (nextValue, { asTags = false } = {}) => {
    if (!hostNode?.key || !fieldDef) return;
    setSaving(true);
    try {
      if (asTags) {
        const tags = Array.isArray(nextValue) ? nextValue : [];
        await updateNode(hostNode.key, { tags }, { actor: user });
        onHostNodeChange?.({ tags });
      } else {
        const patch = buildNodeFieldPatch(hostNode, fieldDef, typeKey, nextValue);
        if (!patch) return;
        await updateNode(hostNode.key, patch, { actor: user });
        onHostNodeChange?.(patch);
      }
      close();
    } catch (error) {
      console.warn('[NodeMissionTaskChecklistActionModal] save failed:', error);
    } finally {
      setSaving(false);
    }
  }, [close, fieldDef, hostNode, onHostNodeChange, typeKey, user]);

  const handleSetOpen = useCallback((open) => {
    if (!open) close();
  }, [close]);

  if (!isOpen || !hostNode?.key || !fieldDef || !modalType) {
    return null;
  }

  const selectOpen = modalType === 'enum' || modalType === 'pipeline';

  return (
    <>
      <NodeSelectModal
        fieldName={fieldDef?.name || ''}
        isOpen={selectOpen}
        label={fieldLabel}
        node={hostNode}
        setOpen={handleSetOpen}
        value={fieldValue || ''}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeMultiselectModal
        fieldName={fieldDef?.name || ''}
        isOpen={modalType === 'multiselect'}
        label={fieldLabel}
        node={hostNode}
        setOpen={handleSetOpen}
        value={fieldValue || []}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeDateModal
        isOpen={modalType === 'date'}
        setOpen={handleSetOpen}
        value={fieldValue}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeDateRangeModal
        isOpen={modalType === 'daterange'}
        setOpen={handleSetOpen}
        value={fieldValue}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeDateTimeModal
        isOpen={modalType === 'datetime'}
        setOpen={handleSetOpen}
        value={fieldValue}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeDateTimeRangeModal
        isOpen={modalType === 'datetimerange'}
        setOpen={handleSetOpen}
        value={fieldValue}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeTagsModal
        isOpen={modalType === 'tags'}
        node={hostNode}
        setOpen={handleSetOpen}
        onTagsSaved={(nextTags) => persistValue(nextTags, { asTags: true })}
      />

      <NodePercentageModal
        isOpen={modalType === 'percentage'}
        label={fieldLabel}
        setOpen={handleSetOpen}
        snapTo={fieldDef?.snapTo}
        value={fieldValue}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeMoneyModal
        field={fieldDef}
        isOpen={modalType === 'money'}
        label={fieldLabel}
        setOpen={handleSetOpen}
        value={fieldValue}
        onChange={(nextValue) => persistValue(nextValue)}
      />

      <NodeLocationModal
        isOpen={modalType === 'location'}
        setOpen={handleSetOpen}
        value={fieldValue || null}
        onLocationSaved={(nextValue) => persistValue(nextValue)}
      />

      <MemberPickerModal
        isOpen={modalType === 'people'}
        isSaving={saving}
        label={fieldLabel}
        node={hostNode}
        setOpen={handleSetOpen}
        value={Array.isArray(fieldValue) ? fieldValue : []}
        onChangeAsync={async (nextPeople) => {
          await persistValue(nextPeople);
        }}
      />

      {modalType === 'relation' ? (
        isNodelistAllowMultiple(fieldDef) ? (
          <NodeNodelistModal
            fieldLabel={fieldLabel}
            isOpen
            node={hostNode}
            setOpen={handleSetOpen}
            targetType={String(fieldDef?.targetType || '').trim()}
            displayFields={fieldDef?.displayFields}
            value={normalizeNodelistValue(fieldValue)}
            onChange={(nextValue) => persistValue(nextValue)}
          />
        ) : (
          <NodeNodelistSingleModal
            fieldLabel={fieldLabel}
            isOpen
            node={hostNode}
            setOpen={handleSetOpen}
            showFilter={fieldDef?.showFilter === true}
            targetType={String(fieldDef?.targetType || '').trim()}
            value={normalizeNodelistValue(fieldValue)}
            onChange={(nextValue) => persistValue(nextValue)}
          />
        )
      ) : null}
    </>
  );
});

NodeMissionTaskChecklistActionModal.displayName = 'NodeMissionTaskChecklistActionModal';

export default NodeMissionTaskChecklistActionModal;
